import { resolveFonts, SiteFonts } from "@keepoinc/site-page-react-component";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, LinkButton } from "../../../../components/Button";
import { FreeKeeposLimitModal } from "../../../../components/FreeKeeposLimitModal";
import { PlusOnlyModal } from "../../../../components/PlusOnlyModal";
import { PlusPromo } from "../../../../components/PlusPromo";
import { PlusTag } from "../../../../components/PlusTag";
import SaveBar from "../../../../components/SaveBar";
import { Container as BarContainer, Box as BarBox, StickySpacer } from "../../../../components/SaveBar/styles";
import { notifySaveError } from "../../../../components/SaveErrorNotice";
import { SubHeader } from "../../../../components/SubHeader";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import { AppState, useAppState } from "../../../../contexts/App";
import { useDraftSitePreview } from "../../../../contexts/PreviewSite";
import { useSite } from "../../../../contexts/Site";
import { PAGES_PATH } from "../../../../hooks/CurrentPage";
import { usePublishFlow } from "../../../../hooks/PublishFlow";
import { useQueryUrl } from "../../../../hooks/QueryUrl";
import { categorySearch } from "../../../../model/categories";
import { siteUsesPlusStyle, toFreeStyle } from "../../../../model/plusFeatures";
import { Site } from "../../../../model/site";
import { Background, Theme, getThemeFromSite } from "../../../../model/theme";
import { api, hasActiveSubscription } from "../../../../services/api";
import { contrast, readableOn } from "../../../../utils/photoColor";
import CreationProgress from "../../components/CreationProgress";
import { AlignSection } from "./components/AlignSection";
import { BackgroundFields } from "./components/BackgroundFields";
import { BackgroundKind, BackgroundKinds } from "./components/BackgroundKinds";
import { ButtonStyleFields } from "./components/ButtonStyleFields";
import { ColorField, FieldPair } from "./components/ColorField";
import { FontRow } from "./components/FontRow";
import { Group, GroupReset } from "./components/Group";
import { HeaderFields } from "./components/HeaderFields";
import { PreDefinedColorSection } from "./components/PreDefinedColorSection";
import { CardPreview, IconLook, IconLooks, IconsPreview } from "./components/StylePreviews";
import {
  Container,
  ContentBox,
  Divider,
  FooterRow,
  Groups,
  Note,
  PlusRequiredNotice,
  SaveError,
  ThemesField,
} from "./styles";

/** The colors the Botões, Cartões and Ícones groups edit. */
type ThemeColor = "buttons" | "textButtons" | "cardColor" | "cardText" | "iconsBackground" | "iconsColor";

const StylePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { appState, setAppState } = useAppState();
  const { site, setSite } = useSite();

  // Reached with "Avançar" from the sections: a step of publishing (back to the sections, forward to
  // the address / PLUS page / publishing). Opened from elsewhere, it's a settings screen (save and back)
  const queryUrl = useQueryUrl();
  const isPublishFlow = queryUrl.get("flow") === "publish";
  const publishFlow = usePublishFlow(queryUrl.get("skipPlusOffer") === "1");
  const [isSaving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // The colors the screen opened with (e.g. the template's): "Redefinir" brings them back
  const [initialTheme] = useState<Theme>(() => getThemeFromSite(site as Site));
  const [activeTheme, setActiveTheme] = useState<Theme>(initialTheme);

  // The look's PLUS items: a FREE Keepo (or one whose PLUS was canceled) sees them, with the PLUS tag, and a tap explains
  // it instead of changing anything. While a Keepo is being created (never published) they're all free to try (the AI
  // may have used them already): it's on publishing without PLUS that it comes back here, `plusRequired`, to the
  // simple version (see the PLUS page's "não quero")
  const isPlusReviewLoop = queryUrl.get("plusRequired") === "1";
  const canUsePlusStyle =
    hasActiveSubscription(appState.editingSiteSubscription) || (!appState.published && !isPlusReviewLoop);
  const [plusOnlyFeature, setPlusOnlyFeature] = useState<string>();
  // Fundo: the solid color (the theme's own) or a background over it; picking a kind starts it from the colors
  const backgroundKind: BackgroundKind = activeTheme.background?.type ?? "color";
  // The Welcome's photo: also the top's image (Topo), changed here or there
  // (an inactive Welcome keeps its photo too: the photo is saved there, so it's not lost when it's turned on)
  const welcome = site?.sections.find(section => section.type === "welcome") as
    | { id: string; logo?: string }
    | undefined;
  const welcomePhoto = welcome?.logo;
  const [profilePhoto, setProfilePhoto] = useState(welcomePhoto);
  // The icons' look, chosen in Ícones: the Keepo's own (its theme), and every Redes sociais section's, on any page
  // (a section added later starts with it). A Keepo from before has it only on its section: read from there
  const social = site?.sections.find(section => section.type === "social") as
    | { id: string; iconShape?: IconLook["shape"]; iconFill?: IconLook["fill"] }
    | undefined;
  const savedLook: IconLook = {
    shape: site?.theme?.iconShape ?? social?.iconShape ?? "ROUND",
    fill: site?.theme?.iconFill ?? social?.iconFill ?? "FILLED",
  };
  const [iconLook, setIconLook] = useState<IconLook>(savedLook);
  const lookChanged = iconLook.shape !== savedLook.shape || iconLook.fill !== savedLook.fill;
  // the site with what's chosen here in its sections: the Welcome's photo, the Redes sociais' icon look, and the
  // Alinhamento changed here also aligns the highlights lists (the last change wins: they keep their own otherwise)
  const withPhoto = (each: Site): Site => {
    const alignChanged = layoutAlign !== (site?.layout?.align || "LEFT");
    if ((profilePhoto === welcomePhoto || !welcome) && !lookChanged && !alignChanged) {
      return each;
    }
    const chosen = (section: Site["sections"][number]) =>
      welcome && section.id === welcome.id && profilePhoto !== welcomePhoto
        ? ({ ...section, logo: profilePhoto } as typeof section)
        : section.type === "social" && lookChanged
          ? ({ ...section, iconShape: iconLook.shape, iconFill: iconLook.fill } as typeof section)
          : alignChanged && section.type === "highlights"
            ? ({ ...section, alignment: layoutAlign } as typeof section)
            : section;
    // the highlights of the Keepo's other pages too
    return {
      ...each,
      ...(lookChanged && { theme: { ...each.theme, iconShape: iconLook.shape, iconFill: iconLook.fill } }),
      sections: each.sections.map(chosen),
      ...(each.pages && { pages: each.pages.map(page => ({ ...page, sections: page.sections.map(chosen) })) }),
    };
  };
  // every background image or video this edit went through (the one it started with too): sent with the save,
  // so the API deletes those the Keepo no longer uses
  const mediaFiles = (background?: Background) =>
    background?.type === "image" || background?.type === "video"
      ? [background.url, background.type === "video" ? background.poster : undefined].filter(
          (url): url is string => !!url
        )
      : [];
  const seenFiles = useRef(
    new Set([
      ...mediaFiles(initialTheme.background),
      ...[initialTheme.header?.banner, initialTheme.header?.bannerVideo, initialTheme.header?.bannerPoster].filter(
        (url): url is string => !!url
      ),
    ])
  );
  const setBackground = (background?: Background) => {
    mediaFiles(background).forEach(url => seenFiles.current.add(url));
    setActiveTheme(current => ({ ...current, id: "custom", background }));
  };
  // what to go back to if an image or video is closed without choosing one (the whole theme, as it was)
  const previousTheme = useRef<Theme>(activeTheme);
  // Topo › Destaque: the page's color matched to the photo, and the colors before it (to switch it off)
  const [matchedColor, setMatchedColor] = useState<string>();
  const beforeMatch = useRef<Theme>();
  const matched = !!matchedColor && activeTheme.primary === matchedColor;
  // another color picked (or the Fundo's Redefinir) ends the match: the next one starts from the colors then
  useEffect(() => {
    if (!matched) {
      beforeMatch.current = undefined;
    }
  }, [matched]);
  // what each kind had while editing: going to another one and back finds it as it was (an image added is still
  // there); only what's saved counts, the API drops the files no longer used
  const keptBackgrounds = useRef<Partial<Record<BackgroundKind, Background>>>({});
  const selectBackgroundKind = (kind: BackgroundKind) => {
    if (kind === backgroundKind) {
      return;
    }
    previousTheme.current = activeTheme;
    const current = activeTheme.background;
    // an image or video not added yet has nothing to keep
    if (current && !((current.type === "image" || current.type === "video") && !current.url)) {
      keptBackgrounds.current[current.type] = current;
    }
    const kept = keptBackgrounds.current[kind];
    if (kept) {
      setBackground(kept);
      return;
    }
    const started: Record<BackgroundKind, Background | undefined> = {
      color: undefined,
      gradient: { type: "gradient", from: activeTheme.primary, to: activeTheme.buttons, angle: 180 },
      blur: { type: "blur" },
      // a new image or video starts faint over the page's color (15%), with no darkening (it'd grey the color)
      image: { type: "image", url: "", dim: 0, opacity: 15 },
      pattern: { type: "pattern", pattern: "dot-grid", color: activeTheme.secondary },
      video: { type: "video", url: "", dim: 0, opacity: 15 },
    };
    setBackground(started[kind]);
  };
  const groups = "pages.editor.settings.groups";
  const inputs = "pages.editor.settings.form.advancedCustomization.inputs";
  // PLUS only: the tag beside a group's title explains it; its colors can be seen but not changed
  const plusTag = (feature: string) =>
    !canUsePlusStyle && (
      <button
        type="button"
        className="plus"
        aria-label={t("pages.settings.form.upgrade")}
        onClick={() => setPlusOnlyFeature(feature)}>
        <PlusTag />
      </button>
    );
  const colorsLock = (feature: string) => ({
    locked: !canUsePlusStyle,
    onLocked: () => setPlusOnlyFeature(feature),
  });

  // Two colors of the theme side by side (Botões, Cartões, Ícones); PLUS only, as `feature`
  const colorPair = (feature: string, colors: [ThemeColor, string][]) => (
    <FieldPair>
      {colors.map(([key, label]) => (
        <ColorField
          key={key}
          label={label}
          color={activeTheme[key]}
          onPick={color => setActiveTheme({ ...activeTheme, id: "custom", [key]: color })}
          {...colorsLock(t(feature))}
        />
      ))}
    </FieldPair>
  );

  const [layoutAlign, setLayoutAling] = useState(site?.layout?.align || "LEFT");
  // Rodapé: the "Crie seu Keepo grátis" brand (PLUS hides it; the same field as Configurações)
  const [hideSignature, setHideSignature] = useState(!!site?.hideSignature);

  // The fonts of the titles and of the text (out of the theme: a palette picked keeps them); none yet: Poppins
  // and Inter, as the Keepo looks today
  const [initialFonts] = useState<SiteFonts>(() => resolveFonts(site?.fonts));
  const [fonts, setFonts] = useState<SiteFonts>(initialFonts);

  // Live preview of the theme, alignment and fonts before saving
  useDraftSitePreview(
    useMemo(
      () =>
        site
          ? withPhoto({ ...site, theme: activeTheme, layout: { align: layoutAlign }, hideSignature, fonts } as Site)
          : undefined,
      [site, activeTheme, layoutAlign, hideSignature, fonts, profilePhoto, iconLook] // eslint-disable-line react-hooks/exhaustive-deps
    )
  );

  const hasFontChanges = initialFonts.heading !== fonts.heading || initialFonts.body !== fonts.body;

  // Any color of the theme (all of them can be edited now), the alignment or a font
  // The theme (colors, background, buttons' look) differs from the one the screen opened with
  const hasThemeChanges = JSON.stringify(initialTheme) !== JSON.stringify(activeTheme);
  // Fundo's Redefinir: the page's colors and the background; the top, the buttons, the cards, the icons and the
  // titles have their own groups (and their own Redefinir)
  const pageLook = ({ id, primary, secondary, background }: Theme) => ({ id, primary, secondary, background });
  const hasColorChanges = JSON.stringify(pageLook(initialTheme)) !== JSON.stringify(pageLook(activeTheme));
  const hasUnsavedChanges =
    hasThemeChanges ||
    profilePhoto !== welcomePhoto ||
    lookChanged ||
    (site?.layout?.align || "LEFT") !== layoutAlign ||
    // `hideSignature` is often absent on the saved site: compare it as a boolean
    !!site?.hideSignature !== hideSignature ||
    hasFontChanges;

  // Capa or Revista with no cover yet is the classic (as the API keeps it): saved, and so shown again, as such
  const coverless =
    (activeTheme.header?.style === "banner" || activeTheme.header?.style === "magazine") &&
    !activeTheme.header.banner &&
    !activeTheme.header.bannerVideo;
  // the API drops the cover's files a style doesn't use (and deletes them): the screen's copy must not keep them
  const withoutUnusedCover = (theme: Theme): Theme => {
    const { header } = theme;
    if (!header || header.style === "banner" || header.style === "magazine") {
      return theme;
    }
    const { banner, bannerVideo, bannerPoster, ...rest } = header; // eslint-disable-line @typescript-eslint/no-unused-vars
    return { ...theme, header: rest };
  };
  const styledSite = () =>
    withPhoto({
      ...site,
      theme: withoutUnusedCover(
        coverless ? { ...activeTheme, header: { ...activeTheme.header, style: "classic" } } : activeTheme
      ),
      layout: {
        align: layoutAlign,
      },
      hideSignature,
      fonts,
    } as Site);

  const advanceHandler = async () => {
    const copySite = styledSite();
    setSaving(true);
    setSaveError(false);
    try {
      await api.saveDraft(appState.editingSiteId as string, copySite, [...seenFiles.current]);
    } catch {
      setSaveError(true);
      setSaving(false);
      return;
    }
    // saved: the API deleted the files left behind, so no kind brings one back
    keptBackgrounds.current = {};
    setSite(copySite);
    setAppState({ ...appState, editingSiteModified: true } as AppState);
    setSaving(false);
    // Back here from publishing without PLUS: still PLUS, the PLUS page again (subscribing is the other way on);
    // simple now, on to publishing (the address first if it was never published), past the PLUS offer
    if (isPlusReviewLoop) {
      navigate(
        siteUsesPlusStyle(copySite)
          ? `/subscription/features?site=${appState.editingSiteId}&flow=${appState.published ? "edit" : "create"}`
          : appState.published
            ? "/editor/publish/ready"
            : "/editor/publish?skipPlusOffer=1"
      );
      return;
    }
    await publishFlow.advance(copySite);
  };

  // "Usar a versão simples": every PLUS item of the look back to its standard at once (the photo stays)
  const useSimpleStyle = () => {
    const simple = toFreeStyle(styledSite());
    setActiveTheme(getThemeFromSite(simple));
    setLayoutAling("LEFT");
    setFonts(resolveFonts(undefined));
    setHideSignature(false);
  };
  // the notice of the way back from publishing without PLUS, while the look still has PLUS items (it goes as soon
  // as it hasn't)
  const showPlusRequiredNotice = isPlusReviewLoop && !canUsePlusStyle && siteUsesPlusStyle(styledSite());

  const saveHandler = () => {
    const copySite = styledSite();

    setSite(copySite);

    const copyAppState = {
      ...appState,
      editingSiteModified: true,
    } as AppState;

    // This site already exists (Style is only reachable after Title's first save),
    // so this can't hit the free Keepos limit - just avoid an unhandled rejection now
    // that saveDraft returns a promise.
    api.saveDraft(copyAppState.editingSiteId as string, copySite, [...seenFiles.current]).catch(notifySaveError);
    setAppState(copyAppState);

    navigate(-1);
  };

  return (
    <>
      <Container>
        {/* where the person is in the flow, on top; the screen's own header under it */}
        {isPublishFlow && <CreationProgress step="style" />}
        <SubHeader
          title={t("pages.editor.settings.header.title")}
          // the step before is the pages (creating the Keepo or editing it)
          crumbs={[{ label: t("pages.editor.pages.title"), to: PAGES_PATH }]}
          hasUnsavedChanges={hasUnsavedChanges}
        />

        <ContentBox>
          {showPlusRequiredNotice && (
            <PlusRequiredNotice>
              <PlusPromo
                title={t("pages.editor.settings.plusNotice.title")}
                description={t("pages.editor.settings.plusNotice.description")}
                cta={t("pages.editor.settings.plusNotice.cta")}
                onClick={() =>
                  navigate(
                    `/subscription/features?site=${appState.editingSiteId}&flow=${appState.published ? "edit" : "create"}`
                  )
                }
              />
              <Button type="WHITE" size="LG" block onClick={useSimpleStyle}>
                {t("pages.editor.settings.plusNotice.simple")}
              </Button>
            </PlusRequiredNotice>
          )}
          <Groups>
            {/* Topo: the Keepo's top (classic, hero, banner, a framed photo) and its photo; all but classic PLUS */}
            <Group
              title={t(`${groups}.header`)}
              aside={
                <>
                  {plusTag(t(`${groups}.header`))}
                  {activeTheme.header && (
                    <GroupReset
                      label={t(`${groups}.headerFields.reset`)}
                      onReset={() => setActiveTheme({ ...activeTheme, header: undefined })}
                    />
                  )}
                </>
              }>
              <HeaderFields
                header={activeTheme.header}
                siteId={appState.editingSiteId as string}
                pexelsSearch={categorySearch(site?.category, site?.categoryOther, t)}
                onChange={header => {
                  // the cover's files this edit went through (dropped by the API once unused)
                  [header?.banner, header?.bannerVideo, header?.bannerPoster].forEach(
                    url => url && seenFiles.current.add(url)
                  );
                  setActiveTheme({ ...activeTheme, header });
                }}
                photo={profilePhoto}
                hasWelcome={!!welcome}
                name={(welcome as { exibitionName?: string } | undefined)?.exibitionName}
                onPhoto={setProfilePhoto}
                // the page's color made the photo's, and a plain color under the photo (nothing between them)
                matched={matched}
                // (after the photo is read: on the theme as it is then, whatever changed meanwhile)
                onMatchColor={primary => {
                  setMatchedColor(primary);
                  setActiveTheme(current => {
                    // the colors before, to go back when switched off (the first match keeps them)
                    if (!beforeMatch.current) {
                      beforeMatch.current = current;
                    }
                    // the texts still read on it (4.5:1), the titles' own color too (3:1) or they go with the texts
                    const keepsTitles = !!current.titleColor && contrast(primary, current.titleColor) >= 3;
                    return {
                      ...current,
                      id: "custom",
                      primary,
                      secondary: readableOn(primary, current.secondary),
                      background: undefined,
                      titleColor: keepsTitles ? current.titleColor : undefined,
                    };
                  });
                }}
                onUnmatch={() => {
                  const before = beforeMatch.current;
                  if (before) {
                    setActiveTheme(current => ({
                      ...current,
                      id: before.id,
                      primary: before.primary,
                      secondary: before.secondary,
                      background: before.background,
                      titleColor: before.titleColor,
                    }));
                  }
                  beforeMatch.current = undefined;
                  setMatchedColor(undefined);
                }}
                theme={activeTheme}
                {...colorsLock(t(`${groups}.header`))}
              />
            </Group>
            <Group
              title={t(`${groups}.background`)}
              aside={
                <>
                  {plusTag(t(`${groups}.background`))}
                  {hasColorChanges && (
                    <GroupReset
                      label={t("pages.editor.settings.form.predefinedColors.resetLabel")}
                      onReset={() => setActiveTheme(current => ({ ...current, ...pageLook(initialTheme) }))}
                    />
                  )}
                </>
              }>
              <BackgroundKinds
                value={backgroundKind}
                onSelect={selectBackgroundKind}
                theme={activeTheme}
                profilePhoto={profilePhoto}
                kept={keptBackgrounds.current}
                {...colorsLock(t(`${groups}.background`))}
              />
              {/* an image or video not added yet shows only its modal: no line under the kinds */}
              {!(
                (activeTheme.background?.type === "image" || activeTheme.background?.type === "video") &&
                !activeTheme.background.url
              ) && <Divider />}
              {activeTheme.background ? (
                <BackgroundFields
                  background={activeTheme.background}
                  onChange={setBackground}
                  siteId={appState.editingSiteId as string}
                  profilePhoto={profilePhoto}
                  pageColor={activeTheme.primary}
                  accent={activeTheme.buttons}
                  onPageColor={primary => setActiveTheme({ ...activeTheme, id: "custom", primary })}
                  pexelsSearch={categorySearch(site?.category, site?.categoryOther, t)}
                  onCancel={() => setActiveTheme(previousTheme.current)}
                />
              ) : (
                <>
                  {/* Cor: a ready-made palette (every color of the Keepo), or just the background's color */}
                  <ThemesField>
                    <span className="role">{t(`${groups}.themes`)}</span>
                    <PreDefinedColorSection
                      activeTheme={activeTheme}
                      // a palette brings only colors: the buttons' style, the top and the icons' look stay
                      onSelectTheme={theme =>
                        setActiveTheme({
                          ...theme,
                          buttonStyle: activeTheme.buttonStyle,
                          header: activeTheme.header,
                          iconShape: activeTheme.iconShape,
                          iconFill: activeTheme.iconFill,
                        })
                      }
                    />
                  </ThemesField>
                  {/* half a row, as the other colors */}
                  <FieldPair>
                    <ColorField
                      label={t(`${inputs}.background.label`)}
                      color={activeTheme.primary}
                      onPick={primary => setActiveTheme({ ...activeTheme, id: "custom", primary })}
                      {...colorsLock(t(`${groups}.background`))}
                    />
                  </FieldPair>
                </>
              )}
            </Group>

            <Group title={t(`${groups}.buttons`)} aside={plusTag(t(`${groups}.buttons`))}>
              <ButtonStyleFields
                value={activeTheme.buttonStyle}
                onChange={buttonStyle => setActiveTheme({ ...activeTheme, buttonStyle })}
                theme={activeTheme}
                {...colorsLock(t(`${groups}.buttons`))}
              />
              <Divider />
              {/* Vidro paints buttons with the page's text color: their own colors do nothing then */}
              {activeTheme.buttonStyle?.variant === "glass" ? (
                <Note>{t(`${groups}.glassNote.buttons`)}</Note>
              ) : (
                colorPair(`${groups}.buttons`, [
                  ["buttons", t(`${inputs}.buttonsColor.label`)],
                  ["textButtons", t(`${inputs}.textButtons.label`)],
                ])
              )}
            </Group>

            <Group title={t(`${groups}.cards`)} aside={plusTag(t(`${groups}.cards`))}>
              {/* a link card as the Keepo shows it, then its colors */}
              <CardPreview theme={activeTheme} photo={profilePhoto} />
              <Divider />
              {colorPair(`${groups}.cards`, [
                ["cardColor", t(`${inputs}.cardColor.label`)],
                ["cardText", t(`${inputs}.cardText.label`)],
              ])}
            </Group>

            <Group title={t(`${groups}.icons`)} aside={plusTag(t(`${groups}.icons`))}>
              {/* the social icons as the Keepo shows them, then their colors */}
              <IconsPreview theme={activeTheme} look={iconLook} />
              {/* their look: the Keepo's default, for the Redes sociais it has and any added later */}
              <Divider />
              <IconLooks theme={activeTheme} value={iconLook} onChange={setIconLook} />
              <Divider />
              {colorPair(`${groups}.icons`, [
                ["iconsBackground", t(`${inputs}.iconsBackground.label`)],
                ["iconsColor", t(`${inputs}.iconsColor.label`)],
              ])}
            </Group>

            <Group
              title={t(`${groups}.text`)}
              aside={
                <>
                  {plusTag(t(`${groups}.text`))}
                  {hasFontChanges && (
                    <GroupReset
                      label={t("pages.editor.settings.form.fonts.resetLabel")}
                      onReset={() => setFonts(initialFonts)}
                    />
                  )}
                </>
              }>
              <AlignSection
                value={layoutAlign}
                onSelect={setLayoutAling}
                locked={!canUsePlusStyle}
                onLocked={() => setPlusOnlyFeature(t(`${inputs}.aligment.label`))}
              />
              <Divider />
              <FontRow
                role="heading"
                font={fonts.heading}
                onPickFont={heading => setFonts({ ...fonts, heading })}
                fontLocked={!canUsePlusStyle}
                onFontLocked={() => setPlusOnlyFeature(t("pages.editor.settings.form.fonts.title"))}
                colorLabel={t(`${groups}.titleColor`)}
                // The titles take the text color until they get their own
                color={activeTheme.titleColor ?? activeTheme.secondary}
                onPickColor={titleColor => setActiveTheme({ ...activeTheme, id: "custom", titleColor })}
                colorLocked={!canUsePlusStyle}
                onColorLocked={() => setPlusOnlyFeature(t(`${groups}.text`))}
              />
              <Divider />
              <FontRow
                role="body"
                font={fonts.body}
                onPickFont={body => setFonts({ ...fonts, body })}
                fontLocked={!canUsePlusStyle}
                onFontLocked={() => setPlusOnlyFeature(t("pages.editor.settings.form.fonts.title"))}
                colorLabel={t(`${groups}.textColor`)}
                color={activeTheme.secondary}
                onPickColor={secondary => setActiveTheme({ ...activeTheme, id: "custom", secondary })}
                colorLocked={!canUsePlusStyle}
                onColorLocked={() => setPlusOnlyFeature(t(`${groups}.text`))}
              />
            </Group>

            <Group title={t(`${groups}.footer`)} aside={plusTag(t("pages.settings.form.showSignature"))}>
              <FooterRow>
                <span>{t("pages.settings.form.showSignature")}</span>
                <ToggleSwitch
                  label={t("pages.settings.form.showSignature")}
                  value={!hideSignature}
                  setValue={() => setHideSignature(hidden => !hidden)}
                  disabled={!canUsePlusStyle}
                  onClick={() => setPlusOnlyFeature(t("pages.settings.form.showSignature"))}
                />
              </FooterRow>
            </Group>
          </Groups>
          {plusOnlyFeature && <PlusOnlyModal feature={plusOnlyFeature} onClose={() => setPlusOnlyFeature(undefined)} />}
        </ContentBox>
        {isPublishFlow ? (
          <>
            {publishFlow.showFreeKeeposLimitModal && (
              <FreeKeeposLimitModal
                i18nKeyPrefix="pages.editor.publish.freeKeeposLimitReached"
                onConfirm={() => navigate(`/subscription/features?site=${appState.editingSiteId}`)}
                onCancel={publishFlow.closeFreeKeeposLimitModal}
              />
            )}
            {/* Same fixed bar as the other steps */}
            <StickySpacer />
            <BarBox $sticky>
              {/* in the bar, over the button: seen wherever the page is scrolled */}
              {(saveError || publishFlow.errorMessage) && (
                <SaveError role="alert">{publishFlow.errorMessage || t("pages.editor.settings.errors.save")}</SaveError>
              )}
              <BarContainer>
                <LinkButton onClick={() => navigate(PAGES_PATH)}>{t("pages.editor.templates.back")}</LinkButton>
                <Button
                  type="INFO"
                  size="LG"
                  disabled={isSaving || publishFlow.isPublishing}
                  $loading={isSaving || publishFlow.isPublishing}
                  onClick={() => !isSaving && !publishFlow.isPublishing && advanceHandler()}>
                  {publishFlow.publishesNow
                    ? t("pages.editor.preview.footer.actions.publish")
                    : t("pages.editor.preview.footer.actions.continue")}
                </Button>
              </BarContainer>
            </BarBox>
          </>
        ) : (
          <SaveBar sticky onSave={saveHandler} hasUnsavedChanges={hasUnsavedChanges} />
        )}
      </Container>
    </>
  );
};

export default StylePage;
